<template>
  <div id="vendor-assessment-review-question-detail" data-cy="heading-section">
    <div
      v-if="assessmentQuestion.checklistItemIsHeading"
      :id="headingId"
      class="tw-cursor-pointer tw-select-none tw-pr-[25px] tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center tw-self-stretch"
      @click="collapseCategory"
    >
      <div
        class="tw-flex tw-flex-row tw-justify-start tw-items-center tw-py-[20px]"
      >
        <CustomCheckbox
          :is-checked="
            assessmentQuestionFormatted.length === selectedItems.length
          "
          class="tw-h-[20px] tw-ml-[25px] !tw-mb-0"
          size="20px"
          padding-left-checkbox="35px"
          @update:is-checked="handleHeadingCheck"
        />
        <div class="tw-flex tw-items-center tw-gap-2">
          <div
            class="tw-font-Poppins tw-text-[18px] tw-font-[600] tw-text-[#211F58]"
            data-cy="question-name"
          >
            {{
              assessmentQuestion.checklistItemHeadingLabel ||
              assessmentQuestion.checklistItemName ||
              ""
            }}
          </div>
        </div>
      </div>

      <div
        class="tw-flex tw-items-center tw-gap-[15px] tw-text-[#B7B7B7] tw-text-[11px] tw-leading-[130%]"
      >
        <div v-if="!assessmentQuestionFormatted.length">
          <span>{{
            getComponentUiText("app-component-assessments-no-question-1")
          }}</span>
        </div>
        <div v-else>
          <span>{{ `${assessmentQuestionFormatted.length} ` }}</span>
          <span>{{
            assessmentQuestionFormatted.length > 1
              ? getComponentUiText(
                  "app-component-assessments-multiple-question-counter-1",
                )
              : getComponentUiText(
                  "app-component-assessments-single-question-counter-1",
                )
          }}</span>
          <span>{{
            !isShowEvaluated
              ? `, ${notEvaluatedCount} ${getComponentUiText(
                  "app-component-assessments-review-counter-1",
                )}`
              : ""
          }}</span>
        </div>
        <div
          v-if="isShowEvaluated"
          class="tw-text-[#904BD6] tw-text-[11px] tw-font-medium tw-py-[3px] tw-px-[5px] tw-rounded-[6px] tw-bg-[#FCFCFD] tw-border tw-border-solid tw-border-[#EEE4F9] tw-leading-[130%]"
        >
          {{
            getComponentUiText("app-component-assessments-results-subtitle-1")
          }}
        </div>
        <span class="float-right button-arrow dropdown-button-arrow-img">
          <img
            v-if="showIcon"
            class="dropdown-button-arrow-img"
            src="@/assets/img/arrow_down2.svg"
          />
          <img
            v-else
            class="collap-button-arrow-down-img"
            src="@/assets/img/arrow_down2.svg"
          />
        </span>
      </div>
    </div>
    <div v-show="!showIcon" class="tw-grid tw-grid-cols-1 tw-gap-[10px]">
      <div
        v-for="item in assessmentQuestionFormatted"
        :key="item.checklistItemDateUpdated"
      >
        <VendorAssessmentQuestionAttachments
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.ATTACH"
          :is-show-required-tag="false"
          :checklist-item="item"
          :checklist-id="checklistRequestedId"
          :is-evaluate="true"
          :is-selected-all="
            selectedItems.filter(
              (i) => i.checklistItemId === item.checklistItemId,
            ).length > 0
          "
          @update:selected="(value) => handleQuestionCheck(value, item)"
          @update:answer="handleUpdateAnswer"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
        />
        <VendorAssessmentQuestionYesNo
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.YES_NO"
          :checklist-item="item"
          :is-show-required-tag="false"
          :is-selected-all="
            selectedItems.filter(
              (i) => i.checklistItemId === item.checklistItemId,
            ).length > 0
          "
          :is-evaluate="true"
          @update:selected="(value) => handleQuestionCheck(value, item)"
          @update:answer="handleUpdateAnswer"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
        />
        <VendorAssessmentQuestionTextArea
          v-if="item.checklistItemComplianceTypeId === COMPLIANCE_TYPE.TEXT"
          :checklist-item="item"
          :is-show-required-tag="false"
          :is-evaluate="true"
          :is-selected-all="
            selectedItems.filter(
              (i) => i.checklistItemId === item.checklistItemId,
            ).length > 0
          "
          :placeholder="
            getComponentUiText(
              'app-assessments-vendor-questions-placeholder-add-detail-1',
            )
          "
          @update:selected="(value) => handleQuestionCheck(value, item)"
          @update:answer="handleUpdateAnswer"
          @show-advance-modal="
            (passkey) => showAdvanceModal(item, assessmentQuestion, passkey)
          "
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, Ref, ref, watch } from "vue";
import { getComponentUiText } from "@/utils/utilities";
import CustomCheckbox from "../common/customCheckBox.vue";
import {
  assessmentQuestionAnswerValue,
  CHECKLIST_TYPE,
  COMPLIANCE_TYPE,
  RIGHT_SLIDE_MODAL_NAME,
} from "@/utils/constants";
import VendorAssessmentQuestionAttachments from "./vendorAssessmentQuestionAttachments.vue";
import VendorAssessmentQuestionYesNo from "./vendorAssessmentQuestionYesNo.vue";
import VendorAssessmentQuestionTextArea from "./vendorAssessmentQuestionTextArea.vue";
import store from "@/store";
import { IModalMessage } from "@/interfaces/common/common";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";

const props = defineProps({
  assessmentQuestion: {
    type: Object,
    default: () => ({}),
    required: true,
  },
  id: {
    type: String,
    default: "",
    required: false,
  },
  headingId: {
    type: String,
    default: "",
    required: false,
  },
  resetAllSelectedQuestions: {
    type: Boolean,
    default: false,
  },
});
const assessmentQuestionFormatted: Ref<any> = ref({});
const selectAll: Ref<boolean> = ref(false);
const showIcon: Ref<boolean> = ref(true);
const updatedItems: any[] = [];
const selectedItems: any = ref([]);
const notEvaluatedCount: Ref<number> = computed(() => {
  // Show if both complianceTypeValue|override of question does not have value.
  return props.assessmentQuestion.checklistItemChildren?.filter(
    (item: any) =>
      !item.checklistItemComplianceTypeValueOrgId &&
      !item.checklistItemComplianceOverride,
  ).length;
});

const isShowEvaluated: Ref<boolean> = computed(() => {
  // Show if one of complianceTypeValue|override all question have value.
  return props.assessmentQuestion.checklistItemChildren?.every(
    (item: any) =>
      item.checklistItemComplianceTypeValueOrgId ||
      item.checklistItemComplianceOverride,
  );
});

const collapseCategory = () => (showIcon.value = !showIcon.value);

const emits = defineEmits(["update-selected"]);
const handleHeadingCheck = (value: boolean) => {
  if (value) {
    const unSelectedItems = assessmentQuestionFormatted.value.filter(
      (item: any) =>
        !selectedItems.value.some(
          (i: any) => i.checklistItemId === item.checklistItemId,
        ),
    );
    selectedItems.value = [...assessmentQuestionFormatted.value];
    emits("update-selected", unSelectedItems);
  } else {
    emits("update-selected", selectedItems.value);
    selectedItems.value = [];
  }
};

watch(
  () => props.resetAllSelectedQuestions,
  (value) => {
    selectedItems.value = [];
  },
);
const handleQuestionCheck = (value: any, item: any) => {
  if (value.isSelected) {
    selectedItems.value.push(item);
  } else {
    const index = selectedItems.value.findIndex(
      (i) => i.checklistItemId === item.checklistItemId,
    );
    if (index > -1) selectedItems.value.splice(index, 1);
  }
  emits("update-selected", [item]);
};

const handleUpdateAnswer = (value) => {
  const existingItem = updatedItems.find(
    (item) => item.checklistRequestedItemId === value.checklistRequestedItemId,
  );

  if (existingItem) Object.assign(existingItem, value);
  else updatedItems.push(value);

  store.commit("Vendor/SET_REQUESTED_UPDATED_ITEMS", updatedItems);
};

const checklistRequestedId = computed(() => {
  return store.state.Vendor.requestedDetailItem.checklistsRequested[0]
    .checklistRequestedId;
});

const showAdvanceModal = (item: any, parent, passkey) => {
  const emitData: IModalMessage = {
    component: RIGHT_SLIDE_MODAL_NAME.ASSESSMENT_REQUESTED_ADVANCED,
    properties: {
      checklistId: checklistRequestedId.value,
      checklistItemId: item.checklistItemId,
      questionData: {
        ...item,
        checklistItemCategoryLabel:
          parent.checklistItemHeadingLabel || parent.checklistItemName || "",
      },
      checklistType: CHECKLIST_TYPE.reviewChecklist,
      passkey,
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_RIGHT_SLIDE_MODAL, emitData);
};

const formatAssessmentQuestion = () => {
  const complianceTypes = store.state.Assessment.assessmentQuestionTypes;
  const questions: any[] =
    props.assessmentQuestion?.checklistItemChildren || [];

  if (questions.length === 0) return;

  for (const item of questions) {
    const complianceType =
      complianceTypes.find(
        (type) => type.id === item?.checklistItemComplianceTypeOrgId,
      )?.complianceTypeValues || [];

    // Update Circle Percent for Evaluate | Override score.
    if (complianceType.length && item.checklistItemComplianceTypeValueOrgId) {
      const type = complianceType.find(
        (i) => i.id === item.checklistItemComplianceTypeValueOrgId,
      );

      if (type) item.itemCompleteValue = type.value * 100;

      if (
        type.label &&
        type.label.indexOf(assessmentQuestionAnswerValue.sufficient) > -1
      )
        item.itemCompleteValue = undefined;
    }

    // This block should be placed last because it affects priority.
    if (item.checklistItemComplianceOverride) {
      item.itemCompleteValue = item.checklistItemComplianceOverride * 100;
    }
  }

  assessmentQuestionFormatted.value = questions;
};

const initial = () => {
  formatAssessmentQuestion();
};

onMounted(() => initial());

watch(
  () => props.assessmentQuestion.checklistItemChildren,
  formatAssessmentQuestion,
);
</script>

<style lang="scss" scoped>
.box-shadow-contain {
  box-shadow: 2px 0px 20px 0px rgba(33, 31, 88, 0.1);
}

.search-box {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  padding: 11px 15px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  max-width: 450px;
  width: 100%;
  outline: none;
  background: white;
  margin-right: 15px;
}

.search-box input {
  border: none;
  outline: none;
  width: 100%;
  margin-left: 10px;
}
.toggle-switch-view {
  border-radius: 6px;
  background: var(--ui-white, #fff);
  backdrop-filter: blur(6px);
  // padding: 4px;
  align-items: center;
  display: inline-flex;

  label {
    margin-bottom: 0;
  }
  .label {
    display: flex;
    cursor: pointer;
    padding: 10px 18px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0;
    color: var(--ui-light-grey, #b7b7b7);
    letter-spacing: 1px;
    border-radius: 6px;
    min-width: 16px;
    // Keep this coded out for now

    // min-width: 111px;
    // background: var(--brand-navy, #211F58);
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;

    &:checked + .label {
      background: var(--brand-navy, #211f58);
      color: #fff;
    }
  }
  &.setting-option-selected {
    background: var(--brand-navy, #211f58);
  }
}
.collap-button-arrow-down-img {
  transform: rotate(180deg);
}
</style>
